import React, { memo, useCallback, useEffect } from 'react';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';

import config from '../config';
import { showError } from '../utils/MessagePopup';
import { isResearcher } from '../utils/checkPermissions';
import { TagManagement } from '../components/TagManagement';
import { fetchTags, deleteTag, lookup, sortTag } from '../actions/tag';
import confirm from './decorators/confirm';
import downloadFile from '../utils/downloadFile';
import { usePopupContext } from '../contexts/PopupContext';

const TagManagementContainer = ({ user, history, loading, tags, fetchTags, deleteTag, lookup, sortTag }) => {
  const popupContext = usePopupContext();
  const { openPopup, closePopup } = popupContext;

  const fetch = useCallback(() => {
    fetchTags().then(result => lookup(result[1].tagTypes, result[2].data));
  }, [fetchTags, lookup]);

  const handleDeleteEvent = useCallback(
    (event, data) => {
      deleteTag(data.id);
      closePopup();
    },
    [deleteTag, closePopup],
  );

  const handleDelete = useCallback(
    (...args) => {
      confirm('Delete this category?', handleDeleteEvent, popupContext)(...args);
    },
    [handleDeleteEvent, popupContext],
  );

  const handleDeleteParent = useCallback(
    (...args) => {
      confirm(
        'WARNING: You are about to delete a Broad Industry Category. Doing so will also delete all Sub' +
          'Categories, End-Markets, Themes, and Business Functions categorized under this Broad Industry Category.' +
          'Are you sure?',
        handleDeleteEvent,
        popupContext,
      )(...args);
    },
    [handleDeleteEvent, popupContext],
  );

  const handleEdit = useCallback(
    (event, data) => {
      openPopup('EditTagPopup', {
        id: data.id,
        category: data.category,
        parentId: data.parentId,
        tagTypeId: data.tagTypeId,
        onSave: fetch,
      });
    },
    [openPopup, fetch],
  );

  const handleAdd = useCallback(() => {
    openPopup('EditTagPopup', { onSave: fetch });
  }, [openPopup, fetch]);

  const handleExport = useCallback(() => {
    const url = `${config.API_BASE_URL}/api/v1/industry_categories/export/`;

    downloadFile({ url });
  }, []);

  const handleClick = useCallback(
    name => {
      sortTag(name);
    },
    [sortTag],
  );

  useEffect(() => {
    const canAccess = user && !isResearcher(user.getIn(['roles', 0, 'slug'], null));

    if (!canAccess) {
      showError(openPopup, config.permisionError, () => {
        history.push('/');
        closePopup();
      });
    } else {
      fetch();
    }
  }, []);

  if (!user || isResearcher(user.getIn(['roles', 0, 'slug'], null))) return null;

  const slug = user.getIn(['roles', 0, 'slug'], null);

  return (
    <div className="full-height">
      <Helmet title="Tag Management Page" />
      <TagManagement
        isSuper={slug === config.ADMINISTRATOR}
        loading={loading}
        onAdd={handleAdd}
        onClick={handleClick}
        onDelete={handleDelete}
        onDeleteParent={handleDeleteParent}
        onEdit={handleEdit}
        onExport={handleExport}
        tags={tags}
        user={user}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  loading: state.tags.get('loading') || state.tags.get('parentTagsLoading') || state.tags.get('typesLoading'),
  tags: state.tags.get('tags'),
  user: state.auth.get('user', null),
});

export default connect(mapStateToProps, {
  fetchTags,
  deleteTag,
  lookup,
  sortTag,
})(memo(TagManagementContainer));
